'use client';

import {
    createContext,
    type ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

export type AppLocale = 'en' | 'de';

type OnboardingPrinciple = {
    title: string;
    desc: string;
};

type LocalizationMessages = {
    settings: {
        documents: string;
        list: string;
        admin: string;
        login: string;
        logout: string;
    };
    chat: {
        chatHistory: string;
        configuration: string;
        contact: {
            button: string;
            title: string;
            expert: string;
            quote: string;
            email: string;
            emailPlaceholder: string;
            message: string;
            expertPlaceholder: string;
            quotePrefill: string;
            cancel: string;
            send: string;
            sending: string;
            sentTitle: string;
            sentText: string;
            requiredFields: string;
            invalidEmail: string;
        };
        history: string;
        searchChats: string;
        newChat: string;
        noChatsYet: string;
        loginToSeeHistory: string;
        now: string;
        today: string;
        yesterday: string;
        last7Days: string;
        older: string;
        chatDeleted: string;
        roomComponents: string;
        searchAndAddProducts: string;
        typeMessage: string;
        answersDisclaimer: string;
        imageUploaded: string;
        uploadFailed: string;
        assistantThinking: string;
        source: string;
        user: string;
        assistant: string;
        sessionNotFound: string;
        failedToChat: string;
        welcomeQuickPrompts: {
            consultant: string[];
            problemSolver: string[];
            compare: string[];
        };
        modeTabs: {
            consultant: string;
            problemSolver: string;
            compare: string;
        };
    };
    upload: {
        invalidType: string;
    };
    config: {
        noConversation: string;
        refreshFailed: string;
        copied: string;
        copiedToClipboard: string;
        title: string;
        statusDraft: string;
        refresh: string;
        projectOverview: string;
        equipment: string;
        items: string;
        signalFlow: string;
        refreshDiagram: string;
        signalFlowDiagramAlt: string;
        renderingDiagram: string;
        notes: string;
        exportPdf: string;
        updatedJustNow: string;
        updatedPrefix: string;
        copy: string;
        copyConfiguration: string;
        userPrefix: string;
        assistantPrefix: string;
        textConfiguration: string;
        textStatus: string;
        textProjectOverview: string;
        textEquipment: string;
        textNotes: string;
        buttonPreparing: string;
        buttonGenerating: string;
        buttonError: string;
        generateConfiguration: string;
        projectLabel: string;
        typeLabel: string;
        dimensionsLabel: string;
        budgetLabel: string;
    };
    onboarding: {
        skip: string;
        subtitle: string;
        description: string;
        mission: string;
        principlesTitle: string;
        principles: OnboardingPrinciple[];
        registerTitle: string;
        registerDescription: string;
        clerkLoginTitle: string;
        clerkLoginHint: string;
        clerkLoginButton: string;
        back: string;
        next: string;
        start: string;
    };
};

const messagesByLocale: Record<AppLocale, LocalizationMessages> = {
    en: {
        settings: {
            documents: 'Documents',
            list: 'List',
            admin: 'Admin',
            login: 'Login',
            logout: 'Logout',
        },
        chat: {
            chatHistory: 'Chat history',
            configuration: 'Configuration',
            contact: {
                button: 'Contact',
                title: 'Contact',
                expert: 'Ask Expert',
                quote: 'Request Quote',
                email: 'Email',
                emailPlaceholder: 'Your email address',
                message: 'Message',
                expertPlaceholder: 'Describe your question or project...',
                quotePrefill:
                    'Dear BigScreen team,\n\nReferring to the current Deepthink configuration, I would like to request a non-binding quote.\n\nProject details:\n- Room / Building: \n- Desired timeline: \n- Special requirements: \n\nI look forward to hearing from you.\n\nBest regards',
                cancel: 'Cancel',
                send: 'Send',
                sending: 'Sending...',
                sentTitle: 'Message sent',
                sentText: "We'll get back to you shortly.",
                requiredFields: 'Please fill in email and message.',
                invalidEmail: 'Please enter a valid email address.',
            },
            history: 'History',
            searchChats: 'Search chats...',
            newChat: 'New Chat',
            noChatsYet: 'No chats yet',
            loginToSeeHistory: 'Log in to see history',
            now: 'now',
            today: 'Today',
            yesterday: 'Yesterday',
            last7Days: 'Last 7 Days',
            older: 'Older',
            chatDeleted: 'Chat deleted',
            roomComponents: 'Room Components',
            searchAndAddProducts: 'Search and add products',
            typeMessage: 'Type your message here...',
            answersDisclaimer:
                'Answers may not be correct. Please confirm with expert if you\'re not sure.',
            imageUploaded: 'Image uploaded',
            uploadFailed: 'Upload failed',
            assistantThinking: 'Deepthink is thinking...',
            source: 'Source',
            user: 'User',
            assistant: 'Assistant',
            sessionNotFound: 'Session not found',
            failedToChat: 'Failed to chat with the AI',
            welcomeQuickPrompts: {
                consultant: [
                    'Meeting room design',
                    'LED wall specification',
                    'Auditorium planning',
                    'System diagram',
                ],
                problemSolver: [
                    'HDMI-over-IP latency',
                    'Dante network issue',
                    'Display no signal',
                    'Room control offline',
                ],
                compare: [
                    'Shure MXA920 vs Sennheiser TCC2',
                    'Crestron NVX vs SDVoE',
                    'QSC vs Biamp Tesira',
                    'Absen vs Samsung LED',
                ],
            },
            modeTabs: {
                consultant: 'Consultant',
                problemSolver: 'Problem Solver',
                compare: 'Compare',
            },
        },
        upload: {
            invalidType: 'Please upload an image or PDF file',
        },
        config: {
            noConversation: 'No conversation to extract configuration from',
            refreshFailed: 'Failed to refresh configuration',
            copied: 'Copied',
            copiedToClipboard: 'Configuration copied to clipboard',
            title: 'Configuration',
            statusDraft: 'Draft',
            refresh: 'Refresh configuration',
            projectOverview: 'Project overview',
            equipment: 'Equipment',
            items: 'items',
            signalFlow: 'Signal flow',
            refreshDiagram: 'Refresh diagram',
            signalFlowDiagramAlt: 'Signal flow diagram',
            renderingDiagram: 'Rendering diagram…',
            notes: 'Notes',
            exportPdf: 'Export PDF',
            updatedJustNow: 'Updated just now',
            updatedPrefix: 'Updated',
            copy: 'Copy',
            copyConfiguration: 'Copy configuration',
            userPrefix: 'User',
            assistantPrefix: 'Assistant',
            textConfiguration: 'Configuration',
            textStatus: 'Status',
            textProjectOverview: 'Project overview',
            textEquipment: 'Equipment',
            textNotes: 'Notes',
            buttonPreparing: 'Preparing…',
            buttonGenerating: 'Generating…',
            buttonError: 'Error',
            generateConfiguration: 'Generate configuration',
            projectLabel: 'Project',
            typeLabel: 'Type',
            dimensionsLabel: 'Dimensions',
            budgetLabel: 'Budget',
        },
        onboarding: {
            skip: 'Skip',
            subtitle: 'Your AV Intelligence',
            description:
                'Deepthink is your technical companion for all questions around audiovisual systems. From system design to market overview and troubleshooting, Deepthink helps you make clear and reliable decisions in media technology.',
            mission:
                'The goal is to be the most reliable quality reference point for enterprise AV.',
            principlesTitle: 'Principles',
            principles: [
                {
                    title: 'Maximum reasoning power',
                    desc: 'Deepthink uses a state-of-the-art model for technical reasoning — always in the strongest mode for clear and reliable answers.',
                },
                {
                    title: 'Maximum factual orientation',
                    desc: 'Technical statements are grounded in real manufacturer data, not assumptions.',
                },
                {
                    title: 'Maximum neutrality',
                    desc: 'No manufacturer bias. Every recommendation follows only the best system solution.',
                },
            ],
            registerTitle: 'Register',
            registerDescription: 'Create your access and start with Deepthink.',
            clerkLoginTitle: 'Login',
            clerkLoginHint: 'Use your account to continue with Deepthink.',
            clerkLoginButton: 'Login',
            back: 'Back',
            next: 'Next',
            start: 'Start',
        },
    },
    de: {
        settings: {
            documents: 'Dokumente',
            list: 'Liste',
            admin: 'Admin',
            login: 'Login',
            logout: 'Logout',
        },
        chat: {
            chatHistory: 'Chatverlauf',
            configuration: 'Konfiguration',
            contact: {
                button: 'Anfragen',
                title: 'Anfragen',
                expert: 'Experte fragen',
                quote: 'Angebot anfragen',
                email: 'E-Mail',
                emailPlaceholder: 'Ihre E-Mail-Adresse',
                message: 'Nachricht',
                expertPlaceholder: 'Beschreiben Sie Ihre Frage oder Ihr Projekt...',
                quotePrefill:
                    'Sehr geehrtes BigScreen-Team,\n\nbezugnehmend auf die aktuelle Deepthink-Konfiguration möchte ich gerne ein unverbindliches Angebot anfragen.\n\nProjektdetails:\n- Raum / Gebäude: \n- Gewünschter Umsetzungszeitraum: \n- Besondere Anforderungen: \n\nIch freue mich auf Ihre Rückmeldung.\n\nMit freundlichen Grüßen',
                cancel: 'Abbrechen',
                send: 'Absenden',
                sending: 'Sende...',
                sentTitle: 'Nachricht gesendet',
                sentText: 'Wir melden uns in Kürze bei Ihnen.',
                requiredFields: 'Bitte E-Mail und Nachricht ausfüllen.',
                invalidEmail: 'Bitte eine gültige E-Mail-Adresse eingeben.',
            },
            history: 'Verlauf',
            searchChats: 'Chats durchsuchen...',
            newChat: 'Neuer Chat',
            noChatsYet: 'Noch keine Chats',
            loginToSeeHistory: 'Einloggen, um den Verlauf zu sehen',
            now: 'jetzt',
            today: 'Heute',
            yesterday: 'Gestern',
            last7Days: 'Letzte 7 Tage',
            older: 'Älter',
            chatDeleted: 'Chat gelöscht',
            roomComponents: 'Raumkomponenten',
            searchAndAddProducts: 'Produkte suchen und hinzufügen',
            typeMessage: 'Nachricht eingeben...',
            answersDisclaimer:
                'Antworten können fehlerhaft sein. Bitte bei Unsicherheit mit einem Experten bestätigen.',
            imageUploaded: 'Bild hochgeladen',
            uploadFailed: 'Upload fehlgeschlagen',
            assistantThinking: 'Deepthink denkt nach...',
            source: 'Quelle',
            user: 'Nutzer',
            assistant: 'Assistent',
            sessionNotFound: 'Session nicht gefunden',
            failedToChat: 'Chat mit der KI fehlgeschlagen',
            welcomeQuickPrompts: {
                consultant: [
                    'Meetingraum-Design',
                    'LED-Wand Spezifikation',
                    'Auditorium-Planung',
                    'Systemdiagramm',
                ],
                problemSolver: [
                    'HDMI-over-IP Latenz',
                    'Dante Netzwerkfehler',
                    'Display kein Signal',
                    'Raumsteuerung offline',
                ],
                compare: [
                    'Shure MXA920 vs Sennheiser TCC2',
                    'Crestron NVX vs SDVoE',
                    'QSC vs Biamp Tesira',
                    'Absen vs Samsung LED',
                ],
            },
            modeTabs: {
                consultant: 'Berater',
                problemSolver: 'Problemlösung',
                compare: 'Vergleich',
            },
        },
        upload: {
            invalidType: 'Bitte ein Bild oder eine PDF-Datei hochladen',
        },
        config: {
            noConversation: 'Keine Konversation zum Extrahieren der Konfiguration',
            refreshFailed: 'Konfiguration konnte nicht aktualisiert werden',
            copied: 'Kopiert',
            copiedToClipboard: 'Konfiguration in die Zwischenablage kopiert',
            title: 'Konfiguration',
            statusDraft: 'Entwurf',
            refresh: 'Konfiguration aktualisieren',
            projectOverview: 'Projektübersicht',
            equipment: 'Geräteliste',
            items: 'Geräte',
            signalFlow: 'Signalfluss',
            refreshDiagram: 'Diagramm aktualisieren',
            signalFlowDiagramAlt: 'Signalflussdiagramm',
            renderingDiagram: 'Diagramm wird gerendert…',
            notes: 'Hinweise',
            exportPdf: 'PDF Export',
            updatedJustNow: 'Gerade aktualisiert',
            updatedPrefix: 'Aktualisiert',
            copy: 'Kopieren',
            copyConfiguration: 'Konfiguration kopieren',
            userPrefix: 'Nutzer',
            assistantPrefix: 'Assistent',
            textConfiguration: 'Konfiguration',
            textStatus: 'Status',
            textProjectOverview: 'Projektübersicht',
            textEquipment: 'Geräteliste',
            textNotes: 'Hinweise',
            buttonPreparing: 'Vorbereiten…',
            buttonGenerating: 'Erstellen…',
            buttonError: 'Fehler',
            generateConfiguration: 'Konfiguration erstellen',
            projectLabel: 'Projekt',
            typeLabel: 'Typ',
            dimensionsLabel: 'Abmessungen',
            budgetLabel: 'Budget',
        },
        onboarding: {
            skip: 'Überspringen',
            subtitle: 'Your AV Intelligence',
            description:
                'Deepthink ist Ihr technischer Companion für alle Fragen rund um audiovisuelle Systeme. Von Systemdesign über Marktüberblick bis zur Problemlösung unterstützt Deepthink dabei, klare und belastbare Entscheidungen in der Medientechnik zu treffen.',
            mission:
                'Ziel ist es, der qualitativ verlässlichste Anlaufpunkt für Enterprise-AV zu sein.',
            principlesTitle: 'Prinzipien',
            principles: [
                {
                    title: 'Maximale Denkkraft',
                    desc: 'Deepthink nutzt ein Spitzenmodell für technisches Denken — immer im stärksten Modus für klare, belastbare Antworten.',
                },
                {
                    title: 'Maximale Faktenorientierung',
                    desc: 'Technische Aussagen basieren auf realen Herstellerdaten, nicht auf Vermutungen.',
                },
                {
                    title: 'Maximale Neutralität',
                    desc: 'Keine Herstellerbindung. Jede Empfehlung folgt ausschließlich der besten Systemlösung.',
                },
            ],
            registerTitle: 'Registrieren',
            registerDescription: 'Erstellen Sie Ihren Zugang und starten Sie mit Deepthink.',
            clerkLoginTitle: 'Login',
            clerkLoginHint: 'Melden Sie sich an, um mit Deepthink zu starten.',
            clerkLoginButton: 'Anmelden',
            back: 'Zurück',
            next: 'Weiter',
            start: 'Starten',
        },
    },
};

const LocalizationContext = createContext<{
    locale: AppLocale;
    messages: LocalizationMessages;
    setLocale: (locale: AppLocale) => void;
}>({
    locale: 'en',
    messages: messagesByLocale.en,
    setLocale: () => {
        // no-op default
    },
});

function inferLocaleFromBrowser(): AppLocale {
    if (typeof window === 'undefined') {
        return 'en';
    }

    const lang = window.navigator.language?.toLowerCase() || '';
    return lang.startsWith('de') ? 'de' : 'en';
}

export function LocalizationProvider({ children }: { children: ReactNode }) {
    const [locale, setLocale] = useState<AppLocale>('en');

    useEffect(() => {
        setLocale(inferLocaleFromBrowser());
    }, []);

    const value = useMemo(
        () => ({ locale, messages: messagesByLocale[locale], setLocale }),
        [locale],
    );

    return (
        <LocalizationContext.Provider value={value}>
            {children}
        </LocalizationContext.Provider>
    );
}

export function useLocalization() {
    return useContext(LocalizationContext);
}
